import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { api, type Site, type Client, type Node, type SSLCert, PHP_VERSIONS } from '../lib/api'
import { PageHeader, Button, Table, Modal, Field, Input, Select, Badge, Checkbox, ErrorBox } from '../components/ui'
import { IconPlus, IconExternal } from '../components/icons'
import { RowMenu } from '../components/RowMenu'
import { SiteLogsModal } from '../components/SiteLogsModal'
import { SiteExtrasModal } from '../components/SiteExtrasModal'
import { TaskProgress } from '../components/TaskProgress'
import { FilterBar, FilterSelect, SearchInput } from '../components/Filters'

const HANDLERS: {v: string; l: string; disabled?: boolean}[] = [
  { v: 'nginx-php',    l: 'Nginx + PHP-FPM' },
  { v: 'nginx-apache', l: 'Nginx + Apache (proxy в 127.0.0.1:8080)' },
  { v: 'apache',       l: 'Только Apache' },
  { v: 'static',       l: 'Только статические файлы' },
]
const PHP_MODES: {v: string; l: string; disabled?: boolean}[] = [
  { v: 'fpm', l: 'FastCGI (PHP-FPM)' },
]
const REDIRECT_WWW = [
  { v: 'off',      l: 'Не перенаправлять' },
  { v: 'to_www',   l: 'На www.<домен>' },
  { v: 'from_www', l: 'С www.<домен>' },
]
const AUTOSUBDOMAIN = [
  { v: 'off',    l: 'Отключены' },
  { v: 'dir',    l: 'В отдельном каталоге' },
  { v: 'subdir', l: 'В подкаталоге домена' },
]

type SiteCfgTab = 'nginx' | 'apache'

function SiteConfigModal({ site, onClose, onTask }: {
  site: Site
  onClose: () => void
  onTask: (taskId: string) => void
}) {
  const [tab, setTab] = useState<SiteCfgTab>('nginx')
  const { data: cfg, isLoading, refetch } = useQuery({
    queryKey: ['site-config', site.id, tab],
    queryFn: async () => (await api.get<{ override: string; rendered: string }>(`/sites/${site.id}/config?type=${tab}`)).data,
  })
  const [text, setText] = useState('')
  const [mode, setMode] = useState<'override' | 'rendered'>('rendered')
  useEffect(() => {
    if (!cfg) return
    if (cfg.override) { setMode('override'); setText(cfg.override) }
    else { setMode('rendered'); setText(cfg.rendered) }
  }, [cfg])

  const save = useMutation({
    mutationFn: async () =>
      (await api.put(`/sites/${site.id}/config?type=${tab}`, { config: text })).data,
    onSuccess: (r: any) => { if (r?.task_id) onTask(r.task_id); refetch() },
  })
  const reset = useMutation({
    mutationFn: async () => (await api.put(`/sites/${site.id}/config?type=${tab}`, { config: '' })).data,
    onSuccess: (r: any) => { if (r?.task_id) onTask(r.task_id); refetch() },
  })

  return (
    <Modal open onClose={onClose} title={`Конфиг vhost: ${site.domain}`} size="lg">
      <div className="flex gap-1 mb-3 border-b border-neutral-200 -mt-1">
        {(['nginx', 'apache'] as SiteCfgTab[]).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3.5 py-2 text-[13px] font-medium border-b-2 -mb-px ${
              tab === t ? 'border-neutral-900 text-neutral-900' : 'border-transparent text-neutral-500 hover:text-neutral-800'
            }`}>{t === 'nginx' ? 'Nginx' : 'Apache'}</button>
        ))}
      </div>
      {isLoading ? <div className="h-96 flex items-center justify-center text-neutral-400 text-[12px]">загрузка…</div> : (
        <>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[12px] text-neutral-500">
              {mode === 'override'
                ? <span className="text-amber-700">• Кастомный override — шаблон из настроек игнорируется</span>
                : <span className="text-green-700">• Генерируется автоматически из шаблона</span>}
            </div>
            {cfg?.override && (
              <Button variant="secondary" size="sm" onClick={() => confirm('Сбросить на авто-шаблон?') && reset.mutate()} disabled={reset.isPending}>
                {reset.isPending ? '...' : 'Сбросить к шаблону'}
              </Button>
            )}
          </div>
          <textarea value={text} onChange={e => { setText(e.target.value); setMode('override') }}
            spellCheck={false}
            className="w-full h-96 font-mono text-[12px] leading-[1.5] p-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-900" />
          <div className="mt-3 flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>Закрыть</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending || text === (cfg?.override || cfg?.rendered)}>
              {save.isPending ? 'Сохраняем…' : 'Сохранить и применить'}
            </Button>
          </div>
        </>
      )}
    </Modal>
  )
}

export function SitesPage() {
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [taskId, setTaskId] = useState<string | null>(null)
  const [editSite, setEditSite] = useState<Site | null>(null)
  const [cfgSite, setCfgSite] = useState<Site | null>(null)
  const [logsSite, setLogsSite] = useState<Site | null>(null)
  const [extrasSite, setExtrasSite] = useState<Site | null>(null)

  const { data: sites = [] } = useQuery({ queryKey: ['sites'], queryFn: async () => (await api.get<Site[]>('/sites')).data })
  const { data: clients = [] } = useQuery({ queryKey: ['clients'], queryFn: async () => (await api.get<Client[]>('/clients')).data })
  const { data: nodes = [] } = useQuery({ queryKey: ['nodes'], queryFn: async () => (await api.get<Node[]>('/nodes')).data })
  const clientMap = Object.fromEntries(clients.map(c => [c.id, c]))
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]))

  // Фильтры
  const [fNode, setFNode] = useState<string>('')
  const [fClient, setFClient] = useState<number>(0)
  const [fSSL, setFSSL] = useState<'all'|'https'|'http'>('all')
  const [fStatus, setFStatus] = useState<'all'|'active'|'suspended'>('all')
  const [fSearch, setFSearch] = useState('')

  const filteredSites = useMemo(() => {
    let list = sites
    if (fNode) list = list.filter(s => s.node_id === fNode)
    if (fClient) list = list.filter(s => s.client_id === fClient)
    if (fSSL === 'https') list = list.filter(s => s.ssl_enabled)
    if (fSSL === 'http') list = list.filter(s => !s.ssl_enabled)
    if (fStatus === 'active') list = list.filter(s => !s.is_suspended)
    if (fStatus === 'suspended') list = list.filter(s => s.is_suspended)
    if (fSearch) {
      const q = fSearch.toLowerCase()
      list = list.filter(s => s.domain.toLowerCase().includes(q) ||
        s.aliases.some(a => a.toLowerCase().includes(q)))
    }
    return list
  }, [sites, fNode, fClient, fSSL, fStatus, fSearch])

  const del = useMutation({
    mutationFn: async (id: number) => api.delete(`/sites/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
  })
  const issueLE = useMutation({
    mutationFn: async (id: number) => (await api.post(`/sites/${id}/ssl/issue`)).data,
    onSuccess: (r: any) => setTaskId(r.task_id),
  })
  const suspend = useMutation({
    mutationFn: async (id: number) => (await api.post(`/sites/${id}/suspend`)).data,
    onSuccess: (r: any) => { setTaskId(r.task_id); qc.invalidateQueries({ queryKey: ['sites'] }) },
  })
  const resume = useMutation({
    mutationFn: async (id: number) => (await api.post(`/sites/${id}/resume`)).data,
    onSuccess: (r: any) => { setTaskId(r.task_id); qc.invalidateQueries({ queryKey: ['sites'] }) },
  })

  return (
    <>
      <PageHeader
        title="Сайты"
        subtitle="Виртуальные хосты nginx. PHP-FPM пул на клиента, HTTPS через Let’s Encrypt или ваш сертификат."
        actions={<Button onClick={() => setAddOpen(true)}><IconPlus /> Добавить сайт</Button>}
      />
      <FilterBar>
        <SearchInput value={fSearch} onChange={setFSearch} placeholder="Поиск по домену…" className="min-w-[240px] flex-1" />
        <FilterSelect value={fNode} onChange={setFNode} options={[
          { value: '', label: 'Все ноды' },
          ...nodes.map(n => ({ value: n.id, label: n.name })),
        ]} />
        <FilterSelect value={fClient} onChange={setFClient} options={[
          { value: 0, label: 'Все клиенты' },
          ...clients.map(c => ({ value: c.id, label: c.unix_user })),
        ]} />
        <FilterSelect value={fSSL} onChange={setFSSL} options={[
          { value: 'all',   label: 'Все' },
          { value: 'https', label: 'Только HTTPS' },
          { value: 'http',  label: 'Только HTTP' },
        ]} />
        <FilterSelect value={fStatus} onChange={setFStatus} options={[
          { value: 'all',       label: 'Все статусы' },
          { value: 'active',    label: 'Активные' },
          { value: 'suspended', label: 'Замороженные' },
        ]} />
      </FilterBar>
      <div className="text-[12px] text-neutral-500 mb-2">
        {filteredSites.length !== sites.length ? `Найдено: ${filteredSites.length} из ${sites.length}` : `Всего сайтов: ${sites.length}`}
      </div>
      <Table<Site>
        rows={filteredSites}
        onRowClick={(s) => setEditSite(s)}
        empty={sites.length === 0 ? 'Пока нет сайтов.' : 'Ничего не найдено по фильтрам.'}
        cols={[
          { key: 'd', label: 'Домен', mobileLabel: 'Домен',
            render: (s) => (
              <div className="min-w-0">
                <a href={`http${s.ssl_enabled ? 's' : ''}://${s.domain}`} target="_blank" rel="noreferrer"
                   className="font-medium text-neutral-900 hover:underline inline-flex items-center gap-1">
                  {s.domain} <IconExternal className="w-3 h-3 text-neutral-400" />
                </a>
                {s.aliases.length > 0 && (
                  <div className="text-[11px] text-neutral-500 mt-0.5">+{s.aliases.length} псевдоним{s.aliases.length > 1 ? 'а' : ''}</div>
                )}
              </div>
            )},
          { key: 'n', label: 'Нода', mobileLabel: 'Нода',
            render: (s) => <span className="text-[13px] text-neutral-700">{nodeMap[s.node_id]?.name || s.node_id.slice(0, 8)}</span> },
          { key: 'c', label: 'Клиент',
            render: (s) => <span className="font-mono text-[12px]">{clientMap[s.client_id]?.unix_user || s.client_id}</span> },
          { key: 'php', label: 'PHP',
            render: (s) => s.php_enabled
              ? <Badge tone="purple">{s.php_version} · {s.php_mode}</Badge>
              : <Badge>—</Badge> },
          { key: 'ssl', label: 'SSL',
            render: (s) => s.ssl_enabled
              ? <Badge tone="green">https</Badge>
              : <Badge>http</Badge> },
          { key: 'st', label: 'Статус',
            render: (s) => s.is_suspended ? <Badge tone="yellow">заморожен</Badge> : <Badge tone="green">активен</Badge> },
          { key: 'act', label: '', w: '1%',
            render: (s) => (
              <div className="flex justify-end">
                <RowMenu items={[
                  { label: 'Изменить', onClick: () => setEditSite(s) },
                  { label: 'Конфиг vhost', onClick: () => setCfgSite(s) },
                  { label: 'Логи (live-tail)', onClick: () => setLogsSite(s) },
                  { label: 'Редиректы / Страницы ошибок', onClick: () => setExtrasSite(s) },
                  ...(s.ssl_enabled ? [] : [{ label: 'Выпустить Let’s Encrypt', onClick: () => issueLE.mutate(s.id) }]),
                  { divider: true, label: '', onClick: () => {} },
                  s.is_suspended
                    ? { label: 'Разморозить', onClick: () => resume.mutate(s.id) }
                    : { label: 'Заморозить', onClick: () => confirm(`Заморозить ${s.domain}?`) && suspend.mutate(s.id) },
                  { divider: true, label: '', onClick: () => {} },
                  { label: 'Удалить', danger: true,
                    onClick: () => confirm(`Удалить сайт ${s.domain}?`) && del.mutate(s.id) },
                ]} />
              </div>
            )},
        ]}
      />
      {cfgSite && <SiteConfigModal site={cfgSite} onClose={() => setCfgSite(null)}
        onTask={(t) => { setCfgSite(null); setTaskId(t) }} />}
      {logsSite && <SiteLogsModal site={logsSite} onClose={() => setLogsSite(null)} />}
      {extrasSite && <SiteExtrasModal site={extrasSite} onClose={() => setExtrasSite(null)}
        onTask={(t) => setTaskId(t)} />}
      {addOpen && <SiteFormModal clients={clients} onClose={() => setAddOpen(false)}
        onTask={(t) => { setAddOpen(false); setTaskId(t); qc.invalidateQueries({ queryKey: ['sites'] }) }} />}
      {editSite && <SiteFormModal site={editSite} clients={clients} onClose={() => setEditSite(null)}
        onTask={(t) => { setEditSite(null); setTaskId(t); qc.invalidateQueries({ queryKey: ['sites'] }) }} />}
      {taskId && <TaskProgress taskId={taskId}
        onClose={() => { setTaskId(null); qc.invalidateQueries({ queryKey: ['sites'] }) }} />}
    </>
  )
}

function SiteFormModal({ site, clients, onClose, onTask }: {
  site?: Site; clients: Client[]; onClose: () => void; onTask: (id: string) => void
}) {
  const isEdit = !!site
  const [clientId, setClientId] = useState<number>(site?.client_id ?? (clients[0]?.id || 0))
  const client = clients.find(c => c.id === clientId)

  // IP-адреса этой ноды
  const { data: nodeIPs = [] } = useQuery({
    queryKey: ['ip-addresses', client?.node_id],
    enabled: !!client,
    queryFn: async () => (await api.get<{id:number;ip:string;is_default:boolean}[]>(`/ip-addresses?node_id=${client!.node_id}`)).data,
  })

  // все серты + в компоненте фильтруем по node_id и wildcard-match
  const { data: allCerts = [] } = useQuery({
    queryKey: ['certificates'],
    queryFn: async () => (await api.get<SSLCert[]>('/certificates')).data,
  })
  const nodeCerts = useMemo(() => {
    if (!client) return []
    const dom = (site?.domain || '').toLowerCase()
    return allCerts.filter(c => {
      if (c.node_id !== client.node_id) return false
      if (!dom) return true // при создании — показываем все
      // wildcard match на фронте
      return (c.domains || []).some(d => {
        const dd = d.toLowerCase().replace(/\.$/, '')
        if (dd === dom) return true
        if (dd.startsWith('*.')) {
          const parent = dd.slice(2)
          if (dom === parent) return true
          return dom.endsWith('.' + parent) && !dom.slice(0, -parent.length - 1).includes('.')
        }
        return false
      })
    })
  }, [allCerts, client, site?.domain])

  // SSL: строковое значение селекта
  const initSSL = site?.ssl_enabled
    ? (site.ssl_cert_id ? `id:${site.ssl_cert_id}` : 'letsencrypt')
    : 'none'

  const [f, setF] = useState({
    // Основное
    domain: site?.domain || '',
    aliases: site?.aliases.join(', ') || '',
    email: site?.email || '',
    charset: site?.charset || 'UTF-8',
    dir_index: site?.dir_index || 'index.php index.html',
    // Обработчик
    handler: site?.handler || 'php',
    php_enabled: site?.php_enabled ?? true,
    php_mode: site?.php_mode || 'fpm',
    php_version: site?.php_version || '8.3',
    open_basedir: site?.open_basedir ?? true,
    // IP-адреса
    listen_ips: (site?.listen_ips as string[] | undefined) || [],
    // SSL
    ssl: initSSL,
    redirect_http_to_https: site?.redirect_http_to_https ?? true,
    hsts_enabled: site?.hsts_enabled ?? false,
    // www / автосубдомены
    redirect_www: site?.redirect_www || 'off',
    autosubdomain: site?.autosubdomain || 'off',
    // Оптимизация
    gzip_enabled: site?.gzip_enabled ?? true,
    gzip_level: site?.gzip_level ?? 5,
    cache_enabled: site?.cache_enabled ?? false,
    cache_ttl_sec: site?.cache_ttl_sec ?? 86400,
    ssi_enabled: site?.ssi_enabled ?? false,
    // DDoS
    ddos_shield: site?.ddos_shield ?? false,
    ddos_limit_rps: site?.ddos_limit_rps ?? 25,
    ddos_burst: site?.ddos_burst ?? 100,
    // Логи
    access_log_enabled: site?.access_log_enabled ?? true,
    error_log_enabled: site?.error_log_enabled ?? true,
  })
  const [openSections, setOpenSections] = useState({
    ssl: true, redir: false, optim: false, ddos: false, logs: false,
  })
  const [err, setErr] = useState('')

  const submit = useMutation({
    mutationFn: async () => {
      const sslKind = f.ssl.startsWith('id:') ? 'existing' : f.ssl
      const sslCertID = f.ssl.startsWith('id:') ? +f.ssl.slice(3) : null

      if (isEdit) {
        const payload: any = {
          aliases: f.aliases.split(/[\s,]+/).filter(Boolean),
          handler: f.handler,
          email: f.email,
          charset: f.charset,
          dir_index: f.dir_index,
          php_enabled: ['php','nginx-php','nginx-apache','apache'].includes(f.handler) && f.php_enabled,
          php_mode: f.php_mode,
          php_version: f.php_version,
          open_basedir: f.open_basedir,
          ssl_enabled: sslKind !== 'none',
          ssl_cert_id: sslCertID,
          redirect_http_to_https: f.redirect_http_to_https,
          hsts_enabled: f.hsts_enabled,
          redirect_www: f.redirect_www,
          autosubdomain: f.autosubdomain,
          gzip_enabled: f.gzip_enabled,
          gzip_level: f.gzip_level,
          cache_enabled: f.cache_enabled,
          cache_ttl_sec: f.cache_ttl_sec,
          ssi_enabled: f.ssi_enabled,
          ddos_shield: f.ddos_shield,
          ddos_limit_rps: f.ddos_limit_rps,
          ddos_burst: f.ddos_burst,
          access_log_enabled: f.access_log_enabled,
          error_log_enabled: f.error_log_enabled,
          listen_ips: f.listen_ips,
        }
        return (await api.patch(`/sites/${site!.id}`, payload)).data
      }
      const payload = {
        client_id: clientId,
        domain: f.domain,
        aliases: f.aliases.split(/[\s,]+/).filter(Boolean),
        handler: f.handler,
        php_enabled: ['php','nginx-php','nginx-apache','apache'].includes(f.handler) && f.php_enabled,
        php_mode: f.php_mode,
        php_version: f.php_version,
        listen_ips: f.listen_ips,
        ssl_issue_le: sslKind === 'letsencrypt',
      }
      return (await api.post('/sites', payload)).data
    },
    onSuccess: (r: any) => onTask(r.task_id),
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })

  return (
    <Modal open onClose={onClose} title={isEdit ? `Настройки: ${site!.domain}` : 'Добавить сайт'} size="lg">
      <form onSubmit={(e) => { e.preventDefault(); submit.mutate() }} className="space-y-5">

        {/* --- Основное --- */}
        <Section title="Основное">
          {!isEdit && (
            <Field label="Клиент (владелец)">
              <Select value={clientId} onChange={(e) => setClientId(+e.target.value)}>
                {clients.map(c => <option key={c.id} value={c.id}>{c.unix_user}{c.full_name ? ` — ${c.full_name}` : ''}</option>)}
              </Select>
            </Field>
          )}
          <Field label="Домен">
            <Input value={f.domain} onChange={(e) => setF({ ...f, domain: e.target.value })}
                   placeholder="example.com" required autoFocus disabled={isEdit} />
          </Field>
          <Field label="Псевдонимы" hint="Через запятую или пробел">
            <Input value={f.aliases} onChange={(e) => setF({ ...f, aliases: e.target.value })} placeholder="www.example.com" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Кодировка">
              <Select value={f.charset} onChange={(e) => setF({ ...f, charset: e.target.value })}>
                <option value="off">Не указывать</option>
                <option value="UTF-8">UTF-8</option>
                <option value="WINDOWS-1251">WINDOWS-1251</option>
              </Select>
            </Field>
            <Field label="Индексный файл">
              <Input value={f.dir_index} onChange={(e) => setF({ ...f, dir_index: e.target.value })} placeholder="index.php index.html" />
            </Field>
          </div>
          <Field label="Email администратора (необязательно)">
            <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder={`webmaster@${f.domain || 'example.com'}`} />
          </Field>
        </Section>

        {/* --- Обработчик --- */}
        <Section title="Обработчик">
          <Field label="Обработчик">
            <Select value={f.handler} onChange={(e) => setF({ ...f, handler: e.target.value })}>
              {HANDLERS.map(h => <option key={h.v} value={h.v} disabled={h.disabled}>{h.l}</option>)}
            </Select>
          </Field>
          {(f.handler === 'nginx-php' || f.handler === 'nginx-apache' || f.handler === 'apache' || f.handler === 'php') && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Режим PHP">
                  <Select value={f.php_mode} onChange={(e) => setF({ ...f, php_mode: e.target.value })}>
                    {PHP_MODES.map(m => <option key={m.v} value={m.v} disabled={m.disabled}>{m.l}</option>)}
                  </Select>
                </Field>
                <Field label="Версия PHP">
                  <Select value={f.php_version} onChange={(e) => setF({ ...f, php_version: e.target.value })}>
                    {PHP_VERSIONS.map(v => <option key={v} value={v}>PHP {v}</option>)}
                  </Select>
                </Field>
              </div>
              <Checkbox label="Использовать open_basedir (изоляция файловой системы)"
                        checked={f.open_basedir} onChange={(e) => setF({ ...f, open_basedir: e.target.checked })} />
            </>
          )}
        </Section>

        {/* --- IP-адреса --- */}
        {nodeIPs.length > 0 && (
          <Section title="IP-адреса">
            <div className="text-[12px] text-neutral-500 mb-2">
              На каких IP сайт будет слушать. Пусто = на всех.
            </div>
            <div className="flex flex-wrap gap-2">
              {nodeIPs.map(ip => {
                const on = f.listen_ips.includes(ip.ip)
                return (
                  <button key={ip.id} type="button"
                    onClick={() => setF({ ...f, listen_ips: on ? f.listen_ips.filter((x: string) => x !== ip.ip) : [...f.listen_ips, ip.ip] })}
                    className={`px-3 py-1.5 text-[12px] rounded-md border transition font-mono ${
                      on ? 'bg-black text-white border-black' : 'bg-white border-neutral-300 text-neutral-700 hover:border-neutral-500'
                    }`}>
                    {ip.ip} {ip.is_default && <span className="ml-1 text-[10px] opacity-70">· default</span>}
                  </button>
                )
              })}
            </div>
          </Section>
        )}

        {/* --- SSL --- */}
        <CollapsibleSection title="SSL-сертификат" open={openSections.ssl} onToggle={() => setOpenSections({ ...openSections, ssl: !openSections.ssl })}>
          <Field label="Тип сертификата">
            <Select value={f.ssl} onChange={(e) => setF({ ...f, ssl: e.target.value })}>
              <option value="none">Не использовать SSL</option>
              <option value="letsencrypt">Новый — Let’s Encrypt (автоматически)</option>
              <option value="selfsigned">Новый — самоподписанный</option>
              {nodeCerts.length > 0 && (
                <optgroup label="Существующие сертификаты">
                  {nodeCerts.map(c => (
                    <option key={c.id} value={`id:${c.id}`}>{c.name} ({c.type})</option>
                  ))}
                </optgroup>
              )}
            </Select>
          </Field>
          {f.ssl !== 'none' && (
            <div className="space-y-2">
              <Checkbox label="Перенаправлять HTTP-запросы в HTTPS"
                        checked={f.redirect_http_to_https}
                        onChange={(e) => setF({ ...f, redirect_http_to_https: e.target.checked })} />
              <Checkbox label="Включить HSTS (Strict-Transport-Security)"
                        checked={f.hsts_enabled}
                        onChange={(e) => setF({ ...f, hsts_enabled: e.target.checked })} />
            </div>
          )}
        </CollapsibleSection>

        {/* --- Редиректы --- */}
        <CollapsibleSection title="Редиректы и автосубдомены" open={openSections.redir} onToggle={() => setOpenSections({ ...openSections, redir: !openSections.redir })}>
          <Field label="Редирект домена (www ↔ без-www)">
            <Select value={f.redirect_www} onChange={(e) => setF({ ...f, redirect_www: e.target.value as any })}>
              {REDIRECT_WWW.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
            </Select>
          </Field>
          <Field label="Автосубдомены" hint="Создавать поддомены на лету на основе директорий">
            <Select value={f.autosubdomain} onChange={(e) => setF({ ...f, autosubdomain: e.target.value as any })}>
              {AUTOSUBDOMAIN.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
            </Select>
          </Field>
        </CollapsibleSection>

        {/* --- Оптимизация --- */}
        <CollapsibleSection title="Оптимизация" open={openSections.optim} onToggle={() => setOpenSections({ ...openSections, optim: !openSections.optim })}>
          <div><Checkbox label="GZIP-сжатие ответов"
                    checked={f.gzip_enabled} onChange={(e) => setF({ ...f, gzip_enabled: e.target.checked })} /></div>
          {f.gzip_enabled && (
            <Field label="Уровень сжатия (1–9)">
              <Input type="number" min={1} max={9} value={f.gzip_level}
                     onChange={(e) => setF({ ...f, gzip_level: +e.target.value })} />
            </Field>
          )}
          <div><Checkbox label="Кэш браузера для статических файлов"
                    checked={f.cache_enabled} onChange={(e) => setF({ ...f, cache_enabled: e.target.checked })} /></div>
          {f.cache_enabled && (
            <Field label="Время кэширования (секунды)">
              <Input type="number" value={f.cache_ttl_sec}
                     onChange={(e) => setF({ ...f, cache_ttl_sec: +e.target.value })} />
            </Field>
          )}
          <div><Checkbox label="SSI (Server Side Includes)"
                    checked={f.ssi_enabled} onChange={(e) => setF({ ...f, ssi_enabled: e.target.checked })} /></div>
        </CollapsibleSection>

        {/* --- DDoS --- */}
        <CollapsibleSection title="Защита от DDoS" open={openSections.ddos} onToggle={() => setOpenSections({ ...openSections, ddos: !openSections.ddos })}>
          <div><Checkbox label="Включить ограничение запросов с одного IP"
                    checked={f.ddos_shield} onChange={(e) => setF({ ...f, ddos_shield: e.target.checked })} /></div>
          {f.ddos_shield && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Запросов в секунду" hint="С одного IP-адреса">
                <Input type="number" value={f.ddos_limit_rps}
                       onChange={(e) => setF({ ...f, ddos_limit_rps: +e.target.value })} />
              </Field>
              <Field label="Пик (burst)">
                <Input type="number" value={f.ddos_burst}
                       onChange={(e) => setF({ ...f, ddos_burst: +e.target.value })} />
              </Field>
            </div>
          )}
        </CollapsibleSection>

        {/* --- Логи --- */}
        <CollapsibleSection title="Логи" open={openSections.logs} onToggle={() => setOpenSections({ ...openSections, logs: !openSections.logs })}>
          <div><Checkbox label="Access log (журнал запросов)"
                    checked={f.access_log_enabled} onChange={(e) => setF({ ...f, access_log_enabled: e.target.checked })} /></div>
          <div><Checkbox label="Error log (журнал ошибок)"
                    checked={f.error_log_enabled} onChange={(e) => setF({ ...f, error_log_enabled: e.target.checked })} /></div>
        </CollapsibleSection>

        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-100 -mx-5 px-5">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={submit.isPending}>
            {submit.isPending ? (isEdit ? 'Сохраняем…' : 'Создаём…') : (isEdit ? 'Сохранить' : 'Создать')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">{title}</div>
      {children}
    </section>
  )
}

function CollapsibleSection({ title, open, onToggle, children }: {
  title: string; open: boolean; onToggle: () => void; children: React.ReactNode
}) {
  return (
    <section className="border border-neutral-200 rounded-lg">
      <button type="button" onClick={onToggle}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-neutral-50 transition-colors rounded-lg">
        <span className="text-[13px] font-medium text-neutral-800">{title}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
             className={`text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`}>
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </button>
      {open && <div className="px-4 pb-4 space-y-3 border-t border-neutral-100">{children}</div>}
    </section>
  )
}
